<template>
  <!-- Edit your own profile, and change your password if you sign in with one (US 9391) -->
  <b-modal ref="panel"
           modal-class="us-drawer"
           hide-header
           hide-footer
           @hidden="reset"
  >
    <div v-if="user" class="us-drawer__inner" qid="profile-edit-panel">
      <header class="us-drawer__head">
        <div class="us-drawer__who">
          <h2 class="us-drawer__name">{{ $t('profile_page.edit.title') }}</h2>
          <div class="us-drawer__meta">{{ $t('profile_page.edit.subtitle') }}</div>
        </div>
        <button type="button"
                class="us-icon-btn us-drawer__close"
                :aria-label="$t('profile_page.edit.cancel')"
                @click="close">
          <font-awesome-icon icon="times"/>
        </button>
      </header>

      <div class="us-drawer__body">
        <b-alert v-if="error"
                 show
                 variant="danger"
                 qid="profile-edit-error">{{ error }}</b-alert>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('profile_page.edit.section_photo') }}</h3>
          <div class="pf-photo-row">
            <span class="us-avatar us-avatar--lg" :style="{ backgroundImage: 'url(' + avatarUrl + ')' }"></span>
            <label class="pf-btn pf-btn--secondary" qid="profile-edit-upload">
              <font-awesome-icon icon="cloud-upload-alt"/>
              {{ hasPhoto ? $t('profile_page.edit.change_photo') : $t('profile_page.edit.upload_photo') }}
              <input type="file"
                     accept="image/png,image/jpeg,image/gif"
                     class="d-none"
                     @change="pickPhoto">
            </label>
            <button v-if="hasPhoto"
                    type="button"
                    class="us-link pf-link--danger"
                    qid="profile-edit-remove-photo"
                    @click="removePhoto">
              {{ $t('profile_page.edit.remove_photo') }}
            </button>
          </div>
          <div class="pf-hint">{{ $t('profile_page.edit.photo_hint') }}</div>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('profile_page.edit.section_details') }}</h3>
          <label class="pf-field">
            <span class="pf-field__label">{{ $t('profile_page.edit.name') }}</span>
            <b-form-input v-model="form.name"
                          :placeholder="$t('profile_page.edit.name')"
                          qid="profile-edit-name"/>
          </label>
          <div class="pf-row2">
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('profile_page.edit.email') }}</span>
              <span class="pf-readonly"><font-awesome-icon icon="lock"/>{{ user.email }}</span>
            </label>
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('profile_page.edit.phone') }}</span>
              <b-form-input v-model="form.phone"
                            :placeholder="$t('profile_page.edit.phone')"
                            qid="profile-edit-phone"/>
            </label>
          </div>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('profile_page.edit.section_sign_in') }}</h3>
          <div v-if="ssoMethod" class="us-note us-note--info" qid="profile-edit-sso-note">
            <font-awesome-icon icon="info-circle"/>
            <span>{{ $t('profile_page.edit.sso_note', { method: $t('users_page.sign_in.' + ssoMethod) }) }}</span>
          </div>
          <template v-else>
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('profile_page.edit.current_password') }}</span>
              <b-form-input v-model="passwordForm.password"
                            type="password"
                            autocomplete="current-password"
                            qid="profile-edit-current-password"/>
            </label>
            <div class="pf-row2">
              <label class="pf-field">
                <span class="pf-field__label">{{ $t('profile_page.edit.new_password') }}</span>
                <b-form-input v-model="passwordForm.new_password"
                              type="password"
                              autocomplete="new-password"
                              :state="passwordForm.new_password ? newPasswordValid : null"
                              qid="profile-edit-new-password"/>
              </label>
              <label class="pf-field">
                <span class="pf-field__label">{{ $t('profile_page.edit.repeat_password') }}</span>
                <b-form-input v-model="passwordForm.new_password_confirmation"
                              type="password"
                              autocomplete="new-password"
                              :state="passwordForm.new_password_confirmation ? repeatValid : null"
                              qid="profile-edit-repeat-password"/>
              </label>
            </div>
            <div class="pf-hint" :class="{ 'pf-hint--ok': newPasswordValid }">
              {{ newPasswordValid ? $t('reset_password.notice.looks_good') : $t('reset_password.notice.password_requirement') }}
            </div>
            <div v-if="passwordForm.new_password_confirmation && !repeatValid" class="pf-hint pf-hint--error">
              {{ $t('reset_password.notice.do_not_match') }}
            </div>
          </template>
        </section>
      </div>

      <footer class="us-drawer__foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="profile-edit-cancel" @click="close">
          {{ $t('profile_page.edit.cancel') }}
        </b-button>
        <b-button variant="success"
                  :disabled="saving || !canSave"
                  qid="profile-edit-save"
                  @click="save">
          <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>
          {{ $t('profile_page.edit.save') }}
        </b-button>
      </footer>
    </div>

    <ImageCropDialog ref="cropDialog"
                     round
                     :title="$t('profile_page.crop.title')"
                     :hint="$t('profile_page.crop.hint')"
                     :save-label="$t('profile_page.crop.save')"
                     @crop="useCroppedPhoto"/>
  </b-modal>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import ImageCropDialog from '@/components/ImageCropDialog.vue';

  // Same limit as the server
  const MAX_PHOTO_SIZE = 10485760;

  export default {
    name: 'ProfileEditPanel',
    components: {
      ImageCropDialog
    },
    data() {
      return {
        user: null,
        form: {},
        passwordForm: {},
        photo: null,
        photoPreview: '',
        removingPhoto: false,
        error: '',
        saving: false
      }
    },
    computed: {
      avatarUrl() {
        if (this.photoPreview) {
          return this.photoPreview;
        }
        return userAvatarUrl(this.removingPhoto ? { id: this.user.id, name: this.form.name } : this.user, true);
      },
      hasPhoto() {
        return !!this.photoPreview || (!!this.user.avatar && !this.removingPhoto);
      },
      ssoMethod() {
        return ['Microsoft', 'Google'].indexOf(this.user.authentication) !== -1 ? this.user.authentication : null;
      },
      wantsPasswordChange() {
        return !this.ssoMethod && !!(this.passwordForm.password || this.passwordForm.new_password || this.passwordForm.new_password_confirmation);
      },
      newPasswordValid() {
        let value = this.passwordForm.new_password || '';
        return value.length > 7 && /[A-Z]/.test(value) && /\W/.test(value);
      },
      repeatValid() {
        return this.passwordForm.new_password === this.passwordForm.new_password_confirmation;
      },
      canSave() {
        if (!this.form.name || !this.form.name.trim()) {
          return false;
        }
        return !this.wantsPasswordChange || (this.passwordForm.password && this.newPasswordValid && this.repeatValid);
      }
    },
    methods: {
      show(user) {
        this.user = user;
        this.form = { name: user.name, phone: user.phone || '' };
        this.$refs.panel.show();
      },
      close() {
        this.$refs.panel.hide();
      },
      reset() {
        this.user = null;
        this.passwordForm = { password: '', new_password: '', new_password_confirmation: '' };
        this.photo = null;
        this.photoPreview = '';
        this.removingPhoto = false;
        this.error = '';
        this.saving = false;
      },
      pickPhoto(event) {
        let file = event.target.files[0];
        event.target.value = '';

        if (!file) {
          return;
        }
        if (file.size > MAX_PHOTO_SIZE) {
          this.error = this.$t('system.file_size');
          return;
        }

        this.error = '';
        this.$refs.cropDialog.open(file);
      },
      // The photo is uploaded with the other changes on Save
      useCroppedPhoto(file) {
        this.photo = file;
        this.photoPreview = URL.createObjectURL(file);
        this.removingPhoto = false;
        this.$refs.cropDialog.close();
      },
      removePhoto() {
        this.photo = null;
        this.photoPreview = '';
        this.removingPhoto = true;
      },
      async save() {
        this.saving = true;
        this.error = '';

        let formData = new FormData();
        formData.append('name', this.form.name.trim());
        formData.append('phone', this.form.phone || '');
        if (this.photo) {
          formData.append('avatar', this.photo);
        } else if (this.removingPhoto) {
          formData.append('avatar', null);
        }
        formData.append('_method', 'put');

        try {
          let response = await piincHttp.post('users/' + this.user.slug, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          let saved = response.data;

          if (this.wantsPasswordChange) {
            let passwordResponse = await piincHttp.post('password/change', this.passwordForm);
            localStorage.setItem('token', passwordResponse.data.token);
            piincHttp.defaults.headers.common['Authorization'] = localStorage.getItem('token');
            saved = passwordResponse.data.user || saved;
          }

          this.$emit('saved', saved);
          this.close();
        } catch (error) {
          this.saving = false;
          let data = (error && error.data) || {};
          this.error = data.message || this.$t('profile_page.edit.error');
          if (data.errors && data.errors.avatar) {
            this.error += ' ' + data.errors.avatar[0];
          }
        }
      }
    }
  }
</script>
